import {
  Grid,
  GridColumn,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import { memo } from "react";
import SmartLink from "components/smartLink/SmartLink";
import Domain from "types/domain";
import styled from "styled-components";
import WhoisButton from "components/whoisButton/WhoisButton";
import ExpireBadge from "components/expireBadge/ExpireBadge";
import { Spinner } from "react-bootstrap";
import moment from "moment";

const StyledGrid = styled(Grid as any)`
  border: solid 1px #e8e8ef;
  margin-top: 20px;
  margin-bottom: 10px;

  .k-grid-header {
    display: none;
  }

  .k-grid-header-wrap {
    display: none;
  }

  .k-grid-table {
    font-size: 1rem;
  }

  .k-grid-content {
    overflow: visible !important;
  }

  .k-grid-content tr {
    overflow: visible;
  }

  .k-grid-content td {
    overflow: visible !important;
  }

  .k-alt,
  .k-table-alt-row,
  .k-master-row.k-alt,
  .k-master-row.k-table-alt-row {
    background-color: rgba(0, 0, 0, 0.04) !important;
  }

  .k-alt:hover,
  .k-table-alt-row:hover,
  .k-master-row.k-alt:hover,
  .k-master-row.k-table-alt-row:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
  }

  .k-master-row:hover,
  .k-grid tbody > tr:not(.k-detail-row):hover,
  .k-grid tbody > tr:not(.k-detail-row).k-hover,
  .k-table-row:hover {
    background-color: inherit !important;
    cursor: default;
  }

  tbody tr:hover {
    background-color: inherit !important;
  }

  .k-pager-nav {
    color: #21b400;
  }

  .k-pager-nav:hover {
    background-color: #e8f5e9;
  }

  .k-state-selected {
    background-color: #21b400;
    color: white;
  }

  .k-button-flat-primary {
    color: #21b400;
  }

  .k-button-flat-primary:hover {
    background-color: #e8f5e9;
    color: #21b400;
  }

  .k-grid-header-wrap,
  .k-grid-content {
    border-collapse: collapse;
  }

  th,
  td {
    border-left: none;
    border-right: none;
  }
`;

const DomainLink = styled(SmartLink)`
  color: #dc3545 !important;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const DomainText = styled.span`
  color: #dc3545;
  font-weight: 500;
`;

const BadgeWrapper = styled.div`
  display: flex;

  @media only screen and (max-width: 576px) {
    display: none;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  min-height: 400px;
`;

interface ExpiredDomainsGridProps {
  data: Domain[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (event: GridPageChangeEvent) => void;
}

const ExpiredDomainsGrid = (props: ExpiredDomainsGridProps) => {
  const { data, total, page, pageSize, loading, onPageChange } = props;

  const DomainCell = (props: any) => {
    const domain = props.dataItem;
    const fullDomain = `${domain.name}.${domain.suffix}`;

    return (
      <td>
        <DomainLink to={`/domain/${fullDomain}`}>{fullDomain}</DomainLink>
      </td>
    );
  };

  const ActionsCell = (props: any) => {
    const domain = props.dataItem;
    const fullDomain = `${domain.name}.${domain.suffix}`;

    return (
      <td
        style={{
          overflow: "visible",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "5px",
          }}
        >
          <BadgeWrapper>
            <ExpireBadge
              expirationDate={
                domain.expiresOn ? moment(domain.expiresOn).toDate() : undefined
              }
            />
          </BadgeWrapper>
          <WhoisButton name={fullDomain} />
        </div>
      </td>
    );
  };

  if (loading) {
    return (
      <LoadingWrapper>
        <Spinner animation="border" variant="success" />
      </LoadingWrapper>
    );
  }

  return (
    <StyledGrid
      data={data}
      total={total}
      skip={(page - 1) * pageSize}
      take={pageSize}
      pageable={{
        buttonCount: 5,
        info: true,
        type: "numeric",
        previousNext: true,
      }}
      onPageChange={onPageChange}
      style={{ height: "auto", minHeight: "400px" }}
    >
      <GridColumn field="name" title="Domain" cell={DomainCell} />
      <GridColumn
        title="Actions"
        cell={ActionsCell}
        width="280px"
        headerClassName="k-text-right"
      />
    </StyledGrid>
  );
};

export default memo(ExpiredDomainsGrid);
